import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as S from './style';

interface GaugeChartProps {
    media_geral: number; // 0 to 100
    media_solidos?: number;
    media_liquidos?: number;
    media_sementes?: number;
    is_empty: boolean;
    size?: number;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({ media_geral, media_solidos, media_liquidos, media_sementes, is_empty, size }) => {
    const radius = 100;
    const stroke = 40;
    const cx = 150; // Centered in 300 width
    const cy = 125;  // Adjusted for height

    // Sections: Red (0-79%) 90° | Yellow (80-94%) 45° | Blue (95-99%) 30° | Green (100%) 15°
    const valueToAngle = (val: number) => {
        const v = Math.min(Math.max(val, 0), 100);
        if (v < 80) return 180 + (v / 80) * 90;           // 0-79%  → 180° to 270°
        else if (v < 95) return 270 + ((v - 80) / 15) * 45; // 80-94% → 270° to 315°
        else if (v < 100) return 315 + ((v - 95) / 5) * 30; // 95-99% → 315° to 345°
        else return 345 + ((v - 100) / 1) * 15;             // 100%   → 345° to 360°
    };

    const sections = useMemo(() => [
        { color: "#dc143c", start: 180, end: 270, tooltip: "Ruim (0% - 79%)", id: "s1" },
        { color: "#ffd700", start: 270, end: 315, tooltip: "Regular (80% - 94%)", id: "s2" },
        { color: "#3c8af4", start: 315, end: 345, tooltip: "Bom (95% - 99%)", id: "s3" },
        { color: "#7ebd61", start: 345, end: 360, tooltip: "Excelente (100%)", id: "s4" },
    ], []);

    // Labels at section boundaries
    const arcLabels = useMemo(() => [
        { label: '0%', angle: 180 },
        { label: '80%', angle: 270 },
        { label: '95%', angle: 315 },
        { label: '100%', angle: 360 },
    ], []);

    const needleAngle = valueToAngle(media_geral);

    const [animatedAngle, setAnimatedAngle] = useState(180);
    const [displayValue, setDisplayValue] = useState(0);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setAnimatedAngle(needleAngle);

            const duration = 1000;
            const start = performance.now();
            const from = 0;
            const to = media_geral;

            const animate = (now: number) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                // ease-out
                const eased = 1 - Math.pow(1 - progress, 3);
                setDisplayValue(Math.round(from + (to - from) * eased));
                if (progress < 1) {
                    rafRef.current = requestAnimationFrame(animate);
                }
            };

            rafRef.current = requestAnimationFrame(animate);
        }, 100);

        return () => {
            clearTimeout(timeout);
            cancelAnimationFrame(rafRef.current);
        };
    }, [needleAngle, media_geral]);

    const needleLength = radius - 10;
    const needleTip = {
        x: cx + needleLength * Math.cos(needleAngle * Math.PI / 180),
        y: cy + needleLength * Math.sin(needleAngle * Math.PI / 180)
    };

    const polarToCartesian = (angle: number, distance: number) => {
        const rad = angle * Math.PI / 180;
        return {
            x: cx + distance * Math.cos(rad),
            y: cy + distance * Math.sin(rad)
        };
    };

    const labelRadius = radius + stroke / 2 + 18;

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    return (
        <S.Container style={size ? { maxWidth: size } : undefined}>
            <svg viewBox="-10 0 320 215">
                {/* Arc sections */}
                {sections.map((section, index) => {
                    const startRad = section.start * Math.PI / 180;
                    const endRad = section.end * Math.PI / 180;

                    const x1 = cx + radius * Math.cos(startRad);
                    const y1 = cy + radius * Math.sin(startRad);

                    const x2 = cx + radius * Math.cos(endRad);
                    const y2 = cy + radius * Math.sin(endRad);

                    const isHovered = hoveredIndex === index;
                    const hasHover = hoveredIndex !== null;

                    return (
                        <path
                            key={index + 'gauge' + section.color}
                            d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
                            fill="none"
                            stroke={is_empty ? '#f5f5f5' : section.color}
                            strokeWidth={!is_empty && isHovered ? stroke + 8 : stroke}
                            style={{
                                opacity: !is_empty && hasHover && !isHovered ? 0.4 : 1,
                                transition: 'stroke-width 0.2s ease, opacity 0.2s ease',
                            }}
                            onMouseEnter={() => !is_empty && setHoveredIndex(index)}
                            onMouseMove={(e) => {
                                if (is_empty) return;
                                const rect = (e.currentTarget.closest('svg') as SVGSVGElement)?.parentElement?.getBoundingClientRect();
                                if (rect) {
                                    setTooltipPos({
                                        x: e.clientX - rect.left,
                                        y: e.clientY - rect.top
                                    });
                                }
                            }}
                            onMouseLeave={() => !is_empty && setHoveredIndex(null)}
                        />
                    );
                })}

                {/* Labels evenly distributed along the arc */}
                {arcLabels.map((item, index) => {
                    const isFirst = index === 0;
                    const isLast = index === arcLabels.length - 1;
                    const labelPos = polarToCartesian(item.angle, labelRadius);

                    // Adjust position for first and last labels
                    if (isFirst) labelPos.x += 15;
                    if (isLast) labelPos.x -= 15;

                    let anchor: string = "middle";
                    if (isFirst) anchor = "end";
                    else if (isLast) anchor = "start";

                    return (
                        <text
                            key={'label-' + index}
                            x={labelPos.x}
                            y={labelPos.y}
                            textAnchor={anchor}
                            alignmentBaseline="middle"
                            fontSize="11"
                            fill="#555"
                            fontWeight="600"
                        >
                            {item.label}
                        </text>
                    );
                })}

                {/* Rounded Caps - always visible */}
                <circle
                    cx={cx + radius * Math.cos(sections[0].start * Math.PI / 180)}
                    cy={cy + radius * Math.sin(sections[0].start * Math.PI / 180)}
                    r={stroke / 2}
                    fill={is_empty ? '#f5f5f5' : sections[0].color}
                    pointerEvents="none"
                />
                <circle
                    cx={cx + radius * Math.cos(sections[sections.length - 1].end * Math.PI / 180)}
                    cy={cy + radius * Math.sin(sections[sections.length - 1].end * Math.PI / 180)}
                    r={stroke / 2}
                    fill={is_empty ? '#f5f5f5' : sections[sections.length - 1].color}
                    pointerEvents="none"
                />

                {/* Needle - always visible */}
                <g
                    style={{
                        transform: `rotate(${animatedAngle}deg)`,
                        transformOrigin: `${cx}px ${cy}px`,
                        transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <path
                        d={`M ${cx - 15} ${cy - 6} L ${cx + radius - 15} ${cy} L ${cx - 15} ${cy + 6} Z`}
                        fill="#333"
                    />
                    <circle cx={cx} cy={cy} r="10" fill="#333" />
                    <circle cx={cx} cy={cy} r="5" fill="#666" />
                </g>

                {/* Percentage value and sub-labels - hidden when empty */}
                {!is_empty && (
                    <>
                        <text x={cx} y={cy + 40} textAnchor="middle" fontSize="25" fontWeight="800" fill="var(--color-text)">
                            {displayValue}%
                        </text>

                        <text x={cx} y={cy + 65} textAnchor="middle" fontSize="13" fill="#888" fontWeight="500">
                            <tspan x={cx - 110} textDecoration={media_solidos === null ? 'line-through' : undefined}>Sólidos: </tspan><tspan fontWeight="700" textDecoration={media_solidos === null ? 'line-through' : undefined}>{Math.round(media_solidos ?? 0)}%</tspan>
                            <tspan x={cx} textDecoration={media_liquidos === null ? 'line-through' : undefined}>Líquidos: </tspan><tspan fontWeight="700" textDecoration={media_liquidos === null ? 'line-through' : undefined}>{Math.round(media_liquidos ?? 0)}%</tspan>
                            <tspan x={cx + 110} textDecoration={media_sementes === null ? 'line-through' : undefined}>Sementes: </tspan><tspan fontWeight="700" textDecoration={media_sementes === null ? 'line-through' : undefined}>{Math.round(media_sementes ?? 0)}%</tspan>
                        </text>
                    </>
                )}
            </svg>

            {hoveredIndex !== null && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        transform: 'translate(calc(-100% - 15px), calc(-100% - 15px))',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13,
                        zIndex: 200,
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {sections[hoveredIndex].tooltip}
                    </span>
                </div>
            )}
        </S.Container>
    );
};
